import type { ViewFieldsFragment } from '@salescore/client-api'
import type { ViewRecordFieldChange } from '@salescore/client-redux'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type ViewQueryList,
} from '@salescore/core'
import { App, Button } from 'antd'
import { Array } from 'effect'
import { t } from 'i18next'
import { useRecoilValue } from 'recoil'

import { buildChangesModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/ChangesModal'
import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { useCursorModel, useCursorMutation } from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import { useRecordsAndChangesMutation } from '../../../recoil/records/mutations/recordsAndChangesMutation'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
// eslint-disable-next-line sonarjs/deprecation
import { getRecordNodeFromRootNode } from '../../../state/nodeUtility'
import { getReferencingModelName } from '../../util/getEditTargetModelName'
import { useUpsertSheetRowMutation, useUpsertSheetRowsMutation } from './upsertSheetRowMutation'

export const useRSheetMutation = (
  rootReference: React.RefObject<HTMLDivElement>,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
) => {
  const { message, modal } = App.useApp()
  const recordsAndChangesMutation = useRecordsAndChangesMutation(compiledConfig, view, query)
  const { clearCursor, moveToAboveInnerRow } = useCursorMutation(ability)
  const cursor = useCursorModel(columns)
  const upsertSheetRowMutation = useUpsertSheetRowMutation(undefined, ability, query)
  const upsertSheetRowsMutation = useUpsertSheetRowsMutation(ability, query)
  const rows = useRecordsImpl()
  const { pickView } = useRecoilValue(viewsContextAtom)
  const isKpiSheet = view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)

  const getRowChanges = (
    column: RSheetColumn<RSheetRecordNode>,
    up: number,
    upInnerRowIndex: number,
    down: number,
    downInnerRowIndex: number,
    isDeleteAllChildren: boolean,
    // eslint-disable-next-line @typescript-eslint/max-params,sonarjs/cognitive-complexity
  ) => {
    const selectedRowAndInnerRowIndexes: Array<{ rowIndex: number; innerRowIndex: number }> = []
    for (const rowIndex of up <= down ? Array.range(up, down) : Array.empty<number>()) {
      const row = rows[rowIndex]
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
      if (row === undefined || column === undefined) {
        continue
      }
      // row から column のオブジェクトを取り出す
      // eslint-disable-next-line sonarjs/deprecation
      const objectRecordNodes = getRecordNodeFromRootNode(row, column.node.path)
      if (objectRecordNodes === undefined) {
        continue
      }
      if (Array.isArray(objectRecordNodes)) {
        if (objectRecordNodes.length === 0) {
          // ありえないはず。この場合は何もしない
          continue
        }
        const startObjectRecordNodeIndex =
          rowIndex === up ? objectRecordNodes.findIndex((node) => node.meta.innerRowIndexStart === upInnerRowIndex) : 0
        const endObjectRecordNodeIndex =
          rowIndex === down
            ? objectRecordNodes.findIndex((node) => node.meta.innerRowIndexStart === downInnerRowIndex)
            : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              objectRecordNodes.at(-1)!.meta.innerRowIndexStart // objectRecordNodes.length !== 0 を上の行で保証済み
        for (const objectRecordNodeIndex of startObjectRecordNodeIndex <= endObjectRecordNodeIndex
          ? Array.range(startObjectRecordNodeIndex, endObjectRecordNodeIndex)
          : Array.empty<number>()) {
          const objectRecordNode = objectRecordNodes[objectRecordNodeIndex]
          if (objectRecordNode === undefined) {
            continue
          }
          selectedRowAndInnerRowIndexes.push({ rowIndex, innerRowIndex: objectRecordNode.meta.innerRowIndexStart })
        }
        continue
      }
      selectedRowAndInnerRowIndexes.push({ rowIndex, innerRowIndex: 0 })
    }
    return selectedRowAndInnerRowIndexes.map(({ rowIndex, innerRowIndex }) => ({
      rowIndex,
      innerRowIndex,
      column,
      value: undefined,
      isDelete: true,
      isDeleteAllChildren,
    }))
  }

  const showDeletionWarningMessage = (changes: ViewRecordFieldChange[]): void => {
    const messageKey = `delete-diff-alert`
    // 2022/03 間違って削除ボタンを押すことがあるので、常にフィードバックが欲しいとのこと
    message.warning({
      key: messageKey,
      content: (
        <span>
          {t(`{{length}}件のレコードを削除します。 `, { length: changes.length })}
          <Button
            type="default"
            onClick={() => {
              if (!isKpiSheet) {
                pickView(view.id)
              }
              modal.confirm(buildChangesModalProperties(compiledConfig, view, query))
              message.destroy(messageKey)
            }}
          >
            {t(`差分を確認`)}
          </Button>
        </span>
      ),
      duration: 6,
    })
  }

  return {
    // eslint-disable-next-line @typescript-eslint/max-params
    async onDestroyRowsFromContextMenu(
      column: RSheetColumn<RSheetRecordNode>,
      up: number,
      upInnerRowIndex: number,
      down: number,
      downInnerRowIndex: number,
      isDeleteAllChildren: boolean,
    ) {
      const rowChanges = getRowChanges(column, up, upInnerRowIndex, down, downInnerRowIndex, isDeleteAllChildren)
      const viewRecordFieldChanges = await upsertSheetRowsMutation(rowChanges)
      showDeletionWarningMessage(viewRecordFieldChanges.changes)
      clearCursor()
      rootReference.current?.focus()
    },
    async onDestroyFromContextMenu(column: RSheetColumn<RSheetRecordNode>, isDeleteAllChildren?: boolean) {
      if (cursor === undefined) {
        return
      }
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: deletable
      const viewRecordFieldChange = await upsertSheetRowMutation({
        rowIndex: cursor.main.rowIndex,
        innerRowIndex: cursor.main.innerRowIndex,
        column,
        value: undefined,
        isDelete: true,
        isDeleteAllChildren,
      })
      showDeletionWarningMessage(viewRecordFieldChange.changes)
      moveToAboveInnerRow()
      rootReference.current?.focus()
    },
    removeRelation(column: RSheetColumn<RSheetRecordNode>) {
      if (cursor === undefined) {
        return
      }
      const referencingModelName = getReferencingModelName({ node: column.node, query })
      if (!ability.canEditModelRecord(referencingModelName)) {
        void message.warning(t(`このシートのレコードを編集する権限がありません`))
        return
      }
      recordsAndChangesMutation.removeRelation({
        rowIndex: cursor.main.rowIndex,
        innerRowIndex: cursor.main.innerRowIndex,
        node: column.node,
      })
    },
  }
}
